import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { assistantErrorStatus, schedulingAssistantApi } from './api';
import { clearAssistantOperations } from './operationRecords';
import { clearAssistantSession } from './session';

import { useAppDispatch } from '@/store/hooks';

export const ASSISTANT_HOME_PATH = '/assistant';

export interface AssistantHomeLocationState {
  assistantSessionExpired?: boolean;
}

/**
 * Ends the assistant session in this tab: forgets the token and any
 * reschedule or cancel records, drops every cached assistant read, and returns
 * to `/assistant`. `expired` tells that page
 * to say the link expired.
 */
export const useEndAssistantSession = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  return useCallback(
    (reason: 'expired' | 'signed_out') => {
      clearAssistantSession();
      clearAssistantOperations();
      const state: AssistantHomeLocationState | undefined =
        reason === 'expired' ? { assistantSessionExpired: true } : undefined;
      navigate(ASSISTANT_HOME_PATH, { replace: true, state });
      dispatch(schedulingAssistantApi.util.resetApiState());
    },
    [dispatch, navigate],
  );
};

/** Any assistant read answering 401 means the session is over. */
export const useEndAssistantSessionOnUnauthorized = (
  ...errors: unknown[]
): void => {
  const endSession = useEndAssistantSession();
  const unauthorized = errors.some(
    (error) => assistantErrorStatus(error) === 401,
  );

  useEffect(() => {
    if (unauthorized) endSession('expired');
  }, [endSession, unauthorized]);
};

export const useSignOutAssistant = () => {
  const endSession = useEndAssistantSession();
  const [signOut, { isLoading }] =
    schedulingAssistantApi.useSignOutAssistantMutation();

  const handleSignOut = useCallback(async () => {
    try {
      await signOut().unwrap();
    } catch {
      // The token is forgotten either way; an unrevoked one expires in 12h.
    }
    endSession('signed_out');
  }, [endSession, signOut]);

  return { isSigningOut: isLoading, signOut: handleSignOut };
};
